import React from 'react';
import { faCheck, faInfo, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mui/material';
import { SxProps } from '@mui/system';
import { styled, Theme } from '@mui/material/styles';
import PageCardTemplate from 'new/templates/PageCardTemplate';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

const iconMap = {
  success: faCheck as IconProp,
  info: faInfo as IconProp,
  warning: faInfo as IconProp,
  error: faTimes as IconProp,
  permission: faTriangleExclamation as IconProp,
};

const getIconColor = (theme: Theme) => ({
  success: theme.palette.success.main,
  info: theme.palette.primary.main,
  warning: theme.palette.warning.main,
  error: theme.palette.error.main,
  permission: theme.palette.warning.main,
});

export type MessageVariant =
  | 'success'
  | 'info'
  | 'warning'
  | 'error'
  | 'permission';

type Props = {
  message: React.ReactNode;
  variant?: MessageVariant;
  hasFullPageLayout?: boolean;
  sx?: SxProps<Theme>;
};

const StyledCardTemplate = styled(PageCardTemplate, {
  shouldForwardProp: (prop) => prop !== 'messageVariant',
})<{
  messageVariant: MessageVariant;
  children: React.ReactNode;
}>(({ messageVariant, theme }) => {
  const backgroundColor = getIconColor(theme)[messageVariant];

  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1.25rem',

    '> .icon': {
      color: '#fff',
      backgroundColor,
      padding: 16,
      width: 67,
      height: 67,
      borderRadius: '50%',
      margin: '20px 0',
    },

    '> .icon-permission': {
      color: '#ffe500',
      margin: '20px 0',
    },
  };
});

const StyledFullPageTemplate = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'messageVariant',
})<{
  messageVariant: MessageVariant;
  children: React.ReactNode;
}>(({ messageVariant, theme }) => {
  const backgroundColor = getIconColor(theme)[messageVariant];

  return {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1.25rem',

    '> .icon': {
      color: '#fff',
      backgroundColor,
      padding: 16,
      width: 67,
      height: 67,
      borderRadius: '50%',
      margin: '20px 0',
    },

    '> .icon-permission': {
      color: '#ffe500',
      margin: '20px 0',
    },
  };
});

const PageMessageTemplate: React.FC<Props> = ({
  message,
  variant = 'info',
  hasFullPageLayout,
  sx,
}) => {
  const Template = hasFullPageLayout
    ? StyledFullPageTemplate
    : StyledCardTemplate;

  const isIcon = () => {
    if (variant == 'permission') {
      return (
        <FontAwesomeIcon
          icon={iconMap[variant]}
          size="3x"
          className="icon-permission"
        />
      );
    } else {
      return (
        <FontAwesomeIcon icon={iconMap[variant]} size="3x" className="icon" />
      );
    }
  };

  return (
    <Template messageVariant={variant} sx={sx}>
      {isIcon()}
      <Box>{message}</Box>
    </Template>
  );
};

export default PageMessageTemplate;
